"use client";

import React from "react";
import { Card, Button, Skeleton, Tooltip } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { createStyles } from "antd-style";
import { useGet, useShaRouting } from "@shesha-io/reactjs";

export interface IWorkflowItemCardProps {
  /** Card heading, e.g. "My Items" */
  title: string;
  /** Short supporting copy shown beneath the title */
  description: string;
  /** Leading glyph shown in the accent tile */
  icon: React.ReactNode;
  /** Accent colour used for the icon tile and count */
  accentColor: string;
  /** Fully-qualified entity type used by the count query */
  entityType: string;
  /** Specification applied so the count reflects the current user's scope */
  specification: string;
  /** Route the "View all" button navigates to */
  route: string;
}

const useStyles = createStyles(({ token, css }) => ({
  card: css`
    height: 100%;
    min-height: 152px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    transition:
      box-shadow 200ms ease,
      border-color 200ms ease;

    &:hover {
      border-color: ${token.colorPrimary};
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    }

    .ant-card-body {
      height: 100%;
      min-height: 152px;
      padding: 0;
      display: flex;
      flex-direction: column;
    }
  `,
  content: css`
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    padding: 22px 28px;
  `,
  left: css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
  `,
  iconTile: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 14px;
    font-size: 24px;
    flex: none;
  `,
  title: css`
    margin: 0 0 4px;
    font-size: 19px;
    font-weight: 600;
    line-height: 1.3;
    color: ${token.colorTextHeading};
  `,
  description: css`
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
    color: ${token.colorTextSecondary};
  `,
  countBlock: css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex: none;
  `,
  count: css`
    font-size: 40px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.02em;
  `,
  countLabel: css`
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${token.colorTextTertiary};
  `,
  footer: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 8px 28px;
    border-top: 1px solid #f0f0f0;
  `,
  viewAll: css`
    height: auto;
    padding: 0;
    font-size: 14px;
    font-weight: 500;

    .anticon {
      font-size: 12px;
    }
  `,
}));

const buildFilter = (specification: string): string =>
  JSON.stringify({ and: [{ is_satisfied: { var: specification } }] });

export const WorkflowItemCard: React.FC<IWorkflowItemCardProps> = ({
  title,
  description,
  icon,
  accentColor,
  entityType,
  specification,
  route,
}) => {
  const { styles } = useStyles();
  const { router, prepareUrl } = useShaRouting();

  // We only need the aggregate count, so request a single row and read totalCount.
  const { data, loading, error } = useGet<any>(
    "/api/services/app/Entities/GetAll",
    {
      queryParams: {
        entityType,
        maxResultCount: 1,
        skipCount: 0,
        properties: "id",
        filter: buildFilter(specification),
      },
    }
  );

  const count: number = data?.result?.totalCount ?? data?.totalCount ?? 0;

  const navigate = (): void => {
    router?.push(prepareUrl(route));
  };

  return (
    <Card className={styles.card}>
      <div className={styles.content}>
        <div className={styles.left}>
          <span
            className={styles.iconTile}
            style={{ backgroundColor: `${accentColor}1A`, color: accentColor }}
          >
            {icon}
          </span>
          <div>
            <h3 className={styles.title}>{title}</h3>
            <p className={styles.description}>{description}</p>
          </div>
        </div>

        <div className={styles.countBlock}>
          {loading ? (
            <Skeleton.Button active size="large" style={{ width: 64, height: 44 }} />
          ) : (
            <Tooltip title={error ? "Unable to load the current count" : undefined}>
              <span className={styles.count} style={{ color: accentColor }}>
                {error ? "—" : count}
              </span>
            </Tooltip>
          )}
          <span className={styles.countLabel}>items</span>
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          type="link"
          className={styles.viewAll}
          icon={<RightOutlined />}
          iconPosition="end"
          onClick={navigate}
          aria-label={`View all ${title}`}
        >
          View all
        </Button>
      </div>
    </Card>
  );
};

export default WorkflowItemCard;
